import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import API from '../../../api/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters, formatDates, getTokenData } from '../../../utils/api';
import { INITIAL_FILTERS_STATE, SPECIAL_OFFERS_PAGE_INFO } from '../constants/specialOffers';
import { showNotification } from '../../../utils/notifications';

export const loadSpecialOffers = createAsyncThunk(
  'specialOffers/loadSpecialOffers',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const { filters } = getState().specialOffersReducer;
      const formattedDates = formatDates(filters, ['startDateFrom', 'startDateTo', 'endDateFrom', 'endDateTo']);
      const formattedFilters = formatFilters(formattedDates);

      const response = await API(getTokenData()).SPECIAL_OFFERS.load({
        ...formattedFilters,
      });

      if (isResponseSuccess(response.status)) {
        return {
          data: response.data.data || [],
          totalItems: response.total || 0,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const createSpecialOffers = createAsyncThunk(
  'specialOffers/createSpecialOffers',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const formattedData = {
        ...(data.name?.trim() && { name: data.name.trim() }),
        ...(data.price !== '' && data.price != null && { price: parseFloat(data.price) }),
        ...(data.ad_spend?.trim() && { ad_spend: data.ad_spend }),
        ...(data.start_date && { start_date: moment(data.start_date).utc().startOf('day').toISOString() }),
        ...(data.end_date && { end_date: moment(data.end_date).utc().startOf('day').toISOString() }),
      };

      const items = {};
      // Bundles
      if (Array.isArray(data.bundles) && data.bundles.length) {
        const validBundles = data.bundles
          .filter(({ id }) => id)
          .map(({ id, count, duration }) => ({
            id,
            ...(count > 0 && { count }),
            ...(duration > 0 && { duration }),
          }));

        if (validBundles.length) items.bundles = validBundles;
      }
      // Plan
      if (data.plan?.id) {
        items.plan = {
          id: data.plan.id,
          ...(data.plan.duration > 0 && { duration: data.plan.duration }),
        };
      }

      if (Object.keys(items).length) {
        formattedData.items = items;
      }

      const response = await API(getTokenData()).SPECIAL_OFFERS.create(
        formattedData
      );
      if (isResponseSuccess(response.status)) {
        return response.data;
      }

      return rejectWithValue(response.data?.error ?? 'Failed to create special offer');
    } catch (error) {
      return rejectWithValue(error.response?.data?.error ?? 'Failed to create special offer');
    }
  },
);

export const updateSpecialOffers = createAsyncThunk(
  'specialOffers/updateSpecialOffers',
  async (data, { getState, rejectWithValue }) => {
    try {
      const formattedData = {
        ...(data.id && { id: data.id }),
        ...(data.start_date && { start_date: moment(data.start_date).utc().startOf('day').toISOString() }),
        ...(data.end_date && { end_date: moment(data.end_date).utc().startOf('day').toISOString() }),
      };

      const api = API(getTokenData());
      const response = await api.SPECIAL_OFFERS.update(formattedData);

      if (isResponseSuccess(response.status)) {
        return response.data;
      }

      return rejectWithValue(response.data?.error ?? 'Failed to update special offer');
    } catch (error) {
      return rejectWithValue(error.response?.data?.error ?? 'Failed to update special offer');
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: SPECIAL_OFFERS_PAGE_INFO.DEFAULT_PAGE_SIZE,
  totalItems: 0,
  status: LOADING_STATUSES.IDLE,
  error: null,
  selected: {},
  filters: INITIAL_FILTERS_STATE,
};

const specialOffersSlice = createSlice({
  name: 'specialOffers',
  initialState,
  reducers: {
    setSelectedSpecialOffer: (state, action) => {
      state.selected = action.payload;
    },
    resetSpecialOffers: () => initialState,
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.pageSize;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSpecialOffers.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadSpecialOffers.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadSpecialOffers.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(createSpecialOffers.fulfilled, () => {
        showNotification({
          type: 'success',
          text: 'Special offer created successfully',
        });
      })
      .addCase(updateSpecialOffers.fulfilled, () => {
        showNotification({
          type: 'success',
          text: 'Special offer updated successfully',
        });
      });
  },
});

export const {
  setSelectedSpecialOffer,
  resetSpecialOffers,
  setFilters,
  setPageData,
} = specialOffersSlice.actions;
export default specialOffersSlice.reducer;

export const specialOffersFilterApply = createAsyncThunk(
  'specialOffers/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadSpecialOffers());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const specialOffersChangePage = createAsyncThunk(
  'specialOffers/changePage',
  async (paginationModel, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData(paginationModel));
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
